import type { EndpointMeta } from "@/apis/types";
import {
  createFetchAndHook,
  type FetchFactory,
  type HookFactory,
} from "@/shared/factories";
import { wsfScheduleApiMeta } from "../apiMeta";
import {
  type ScheduleTodayByTerminalsInput,
  scheduleTodayByTerminalsInputSchema,
} from "./shared/scheduleToday.input";
import { type Schedule, scheduleSchema } from "./shared/scheduleToday.output";

/**
 * Metadata for the fetchScheduleTodayByTerminals endpoint
 */
export const scheduleTodayByTerminalsMeta = {
  functionName: "fetchScheduleTodayByTerminals",
  endpoint:
    "/scheduletoday/{DepartingTerminalID}/{ArrivingTerminalID}/{OnlyRemainingTimes}",
  inputSchema: scheduleTodayByTerminalsInputSchema,
  outputSchema: scheduleSchema,
  sampleParams: {
    DepartingTerminalID: 1,
    ArrivingTerminalID: 10,
    OnlyRemainingTimes: false,
  },
  endpointDescription: "Get today's schedule for a terminal pair.",
} satisfies EndpointMeta<ScheduleTodayByTerminalsInput, Schedule>;

/**
 * Fetch function for retrieving today's schedule for a terminal pair
 */
const { fetch, useHook } = createFetchAndHook<
  ScheduleTodayByTerminalsInput,
  Schedule
>({
  api: wsfScheduleApiMeta,
  endpoint: scheduleTodayByTerminalsMeta,
  getCacheStrategy: () =>
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    require("./shared/scheduleToday.endpoints").scheduleTodayGroup
      .cacheStrategy,
});

/**
 * Fetch function for retrieving today's schedule for a terminal pair
 */
export const fetchScheduleTodayByTerminals: FetchFactory<
  ScheduleTodayByTerminalsInput,
  Schedule
> = fetch;

/**
 * React Query hook for retrieving today's schedule for a terminal pair
 */
export const useScheduleTodayByTerminals: HookFactory<
  ScheduleTodayByTerminalsInput,
  Schedule
> = useHook;
